/**
 * EntityReviewPanel — reusable component for reviewing extracted entities.
 * Displays cards with type, name, details, confidence badges, and inline editing.
 * Used by HomebrewImportDialog and potentially by Spec D campaign import.
 *
 * Uses shadcn primitives (Checkbox / Select / Badge / Button / Input) for
 * design-system consistency with the rest of the app.
 */

import React, { useState } from 'react';
import { useI18n } from '../../i18n';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

export interface ReviewableEntity {
  type: string;
  slug: string;
  name: string;
  data: Record<string, unknown>;
  sourceText: string;
  confidence: number;
  selected: boolean;
}

/** Default type labels for homebrew entities */
export const HOMEBREW_TYPE_LABELS: Record<string, string> = {
  class: 'Class',
  subclass: 'Subclass',
  spell: 'Spell',
  feat: 'Feat',
  race: 'Race',
  item: 'Item',
};

/** Type labels for campaign import entities */
export const CAMPAIGN_TYPE_LABELS: Record<string, string> = {
  npc: 'NPC',
  location: 'Location',
  note: 'Note',
  journal: 'Journal',
  encounter: 'Encounter',
};

interface EntityReviewPanelProps {
  entities: ReviewableEntity[];
  onToggle: (index: number) => void;
  onUpdate: (index: number, changes: Partial<ReviewableEntity>) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  /** Custom type labels map. Defaults to HOMEBREW_TYPE_LABELS */
  typeLabels?: Record<string, string>;
  /** Available type options for type change dropdown (if provided, shows dropdown) */
  availableTypes?: string[];
  /** Callback for adding a manual entity */
  onAddManual?: () => void;
  /** Callback for retrying a single entity extraction */
  onRetry?: (index: number) => void;
  /** Callback for retrying all entities below a confidence threshold */
  onRetryBelow?: (threshold: number) => void;
  /** Set of indices currently being retried */
  retryingIndices?: Set<number>;
}

export function EntityReviewPanel({
  entities,
  onToggle,
  onUpdate,
  onSelectAll,
  onDeselectAll,
  typeLabels = HOMEBREW_TYPE_LABELS,
  availableTypes,
  onAddManual,
  onRetry,
  onRetryBelow,
  retryingIndices,
}: EntityReviewPanelProps) {
  const { t } = useI18n();
  const [retryThreshold, setRetryThreshold] = useState(70);
  const belowCount = entities.filter(e => e.confidence < retryThreshold).length;

  return (
    <div className="space-y-2">
      {/* Batch actions bar */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Button variant="link" size="sm" className="h-auto p-0" onClick={onSelectAll}>
          {t('entityReview.selectAll')}
        </Button>
        <Button variant="link" size="sm" className="h-auto p-0" onClick={onDeselectAll}>
          {t('entityReview.deselectAll')}
        </Button>
        {onAddManual && (
          <Button variant="link" size="sm" className="h-auto p-0" onClick={onAddManual}>
            {t('entityReview.addManual')}
          </Button>
        )}
        <span className="ml-auto text-ui text-muted-foreground">
          {entities.filter(e => e.selected).length}/{entities.length} {t('entityReview.selectedCount')}
        </span>
      </div>

      {onRetryBelow && belowCount > 0 && (
        <div className="flex items-center gap-2 rounded bg-muted/50 p-2 text-ui">
          <span className="whitespace-nowrap">{t('entityReview.retryBelow')}</span>
          <input
            type="range"
            min={30}
            max={95}
            step={5}
            value={retryThreshold}
            onChange={e => setRetryThreshold(Number(e.target.value))}
            className="w-24"
          />
          <span className="w-10 text-center font-medium">{retryThreshold}%</span>
          <Button
            size="sm"
            className="px-2"
            onClick={() => onRetryBelow(retryThreshold)}
            disabled={retryingIndices && retryingIndices.size > 0}
          >
            {t('entityReview.retry')} ({belowCount})
          </Button>
        </div>
      )}

      {entities.map((entity, index) => (
        <EntityCard
          key={`${entity.slug}-${index}`}
          entity={entity}
          onToggle={() => onToggle(index)}
          onUpdate={(changes) => onUpdate(index, changes)}
          defaultOpen={entity.confidence < 70}
          typeLabels={typeLabels}
          availableTypes={availableTypes}
          onRetry={onRetry ? () => onRetry(index) : undefined}
          isRetrying={retryingIndices?.has(index) ?? false}
        />
      ))}
    </div>
  );
}

interface EntityCardProps {
  entity: ReviewableEntity;
  onToggle: () => void;
  onUpdate: (changes: Partial<ReviewableEntity>) => void;
  defaultOpen: boolean;
  typeLabels: Record<string, string>;
  availableTypes?: string[];
  onRetry?: () => void;
  isRetrying: boolean;
}

function EntityCard({ entity, onToggle, onUpdate, defaultOpen, typeLabels, availableTypes, onRetry, isRetrying }: EntityCardProps) {
  const [expanded, setExpanded] = useState(defaultOpen);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(entity.name);

  const handleNameSave = () => {
    if (nameValue.trim() && nameValue !== entity.name) {
      onUpdate({ name: nameValue.trim() });
    }
    setEditingName(false);
  };

  return (
    <div className={`rounded border p-3 ${entity.selected ? 'border-primary' : 'border-muted opacity-60'}`}>
      <div className="flex items-center gap-2">
        <Checkbox checked={entity.selected} onCheckedChange={onToggle} />

        {availableTypes ? (
          <Select value={entity.type} onValueChange={(v) => onUpdate({ type: v })}>
            <SelectTrigger className="h-6 w-auto gap-1 px-1.5 py-0.5 text-meta">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableTypes.map(ty => (
                <SelectItem key={ty} value={ty}>{typeLabels[ty] ?? ty}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Badge variant="secondary" className="text-meta">
            {typeLabels[entity.type] ?? entity.type}
          </Badge>
        )}

        {/* Name (editable) */}
        {editingName ? (
          <Input
            className="h-6 flex-1 text-ui"
            value={nameValue}
            onChange={e => setNameValue(e.target.value)}
            onBlur={handleNameSave}
            onKeyDown={e => { if (e.key === 'Enter') handleNameSave(); }}
            autoFocus
          />
        ) : (
          <span
            className="flex-1 cursor-pointer text-ui font-medium"
            onClick={() => setEditingName(true)}
          >
            {entity.name}
          </span>
        )}

        {/* Confidence badge: green >= 70, yellow < 70 */}
        <Badge
          variant="outline"
          className={`text-meta ${entity.confidence >= 70 ? 'border-ok/40 text-ok' : 'border-warn/40 text-warn'}`}
        >
          {isRetrying ? '...' : `${entity.confidence}%`}
        </Badge>

        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            className="w-6 p-0"
            onClick={onRetry}
            disabled={isRetrying}
            title="Retry extraction"
          >
            ↻
          </Button>
        )}

        <Button variant="ghost" size="sm" className="w-6 p-0" onClick={() => setExpanded(!expanded)}>
          {expanded ? '▲' : '▼'}
        </Button>
      </div>

      {expanded && (
        <div className="mt-2 space-y-1 text-meta">
          <div className="text-muted-foreground">Slug: {entity.slug}</div>
          {entity.sourceText && (
            <div className="line-clamp-3 text-muted-foreground">
              Source: {entity.sourceText.slice(0, 200)}
            </div>
          )}
          {Object.entries(entity.data).map(([key, value]) => (
            <div key={key} className="flex gap-1">
              <span className="font-medium">{key}:</span>
              <span>{typeof value === 'string' ? value.slice(0, 100) : JSON.stringify(value).slice(0, 100)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default EntityReviewPanel;
